/**
 * Wire-format types for the {@code renderDataChart} MCP tool. Mirrors the
 * Java {@code ChartSpec} record on the server so what the gateway forwards
 * deserializes 1:1 into TypeScript without a translation layer.
 */

export type ChartKind = "line" | "area" | "bar" | "column" | "scatter" | "pie";
export type AxisType = "temporal" | "ordinal" | "quantitative";
export type Aggregate = "none" | "sum" | "avg" | "count" | "min" | "max";

export type Axis = {
  column: string;
  label: string;
  type: AxisType;
  agg: Aggregate;
};

export type SeriesDef = {
  column: string;
  label: string;
};

export type InsightKind = "trend" | "max" | "min" | "delta" | "outlier" | "gap" | "info";
export type InsightSeverity = "info" | "warn" | "danger";

export type ChartInsight = {
  kind: InsightKind;
  severity: InsightSeverity;
  text: string;
};

export type ChartSpec = {
  kind: ChartKind;
  title: string;
  x: Axis;
  y: Axis;
  series?: SeriesDef | null;
  data: Array<Record<string, unknown>>;
  insights: ChartInsight[];
};

/** Cheap shape check — used to discriminate ToolChart payloads in the SSE
 *  reducer without paying for full validation. Aligns with the gateway's
 *  {@code VisualResponseForwarder.looksLikeChartSpec}. */
export function isChartSpec(value: unknown): value is ChartSpec {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.kind === "string" &&
    typeof v.x === "object" &&
    typeof v.y === "object" &&
    Array.isArray(v.data) &&
    Array.isArray(v.insights)
  );
}

/** The kinds the user can switch between via the tab strip. The server can
 *  ship any kind; the tabs just let the user override. */
export const SWITCHABLE_KINDS: ChartKind[] = ["line", "area", "bar", "column", "pie"];
